import { yupResolver } from '@hookform/resolvers/yup'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { formatUTCDateTime, getDateFromInputDateTime } from 'common/utils/date'
import { endOfToday } from 'date-fns'
import { useGetCompletedPonds, useGetPondNames } from 'modules/pond/api/queries'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { SpeciesName } from 'modules/pond/constants'
import {
  useMutateCreateShrimpTransfer,
  useMutateUpdateShrimpTransfer,
} from 'modules/shrimpTransfer/api/mutation'
import { useGetLatestCostPerKg } from 'modules/shrimpTransfer/api/queries'
import { PartialTransfer } from 'modules/shrimpTransfer/api/types'
import { useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
  Button,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  Box,
  Typography,
} from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'

type FormValues = {
  transfer_date: Date
  transfer_time: Date
  destination_pond_id: string
  amount: string | number
  cost_per_kg: string | number
  amount_per_kg: string | number
}
interface ShrimpTransferRecordDialogProps {
  onClose: () => void
  onViewDialogClose?: () => void
  open: boolean
  record?: PartialTransfer
  mode: 'create' | 'edit'
}

const ShrimpTransferRecordDialog = ({
  onClose,
  onViewDialogClose,
  open,
  record,
  mode,
}: ShrimpTransferRecordDialogProps) => {
  const { t } = useTranslation('shrimpTransfer')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const invalidateCostQueries = useInvalidateCostQueries()
  const validationSchema = Yup.object().shape({
    transfer_date: Yup.date().required(t('transfer.required_date')),
    transfer_time: Yup.date().required(t('transfer.required_time')),
    destination_pond_id: Yup.string().required(t('transfer.required_destination')),

    amount: Yup.number()
      .typeError(t('transfer.required_quantity'))
      .positive(t('transfer.positive_quantity'))
      .required(t('transfer.required_quantity')),

    cost_per_kg: Yup.number()
      .typeError(t('transfer.required_cost', { species }))
      .positive(t('transfer.positive_cost', { species }))
      .required(t('transfer.required_cost', { species })),

    amount_per_kg: Yup.number()
      .typeError(t('transfer.required_shrimp_size', { species }))
      .positive(t('transfer.positive_shrimp_size', { species }))
      .required(t('transfer.required_shrimp_size', { species })),
  })

  const defaultValues = {
    transfer_date: record?.transfer_date ? new Date(record.transfer_date) : new Date(),
    transfer_time: record?.transfer_date ? new Date(record.transfer_date) : new Date(),
    destination_pond_id: record?.destination_pond_id ?? '',
    amount: record?.amount ?? '',
    cost_per_kg: record?.cost_per_kg ?? '',
    amount_per_kg: record?.amount_per_kg ?? '',
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setError,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  })

  const { data: latestCostPerKg, refetch: refetchLatestCostPerKg } = useGetLatestCostPerKg(
    mode === 'create'
  )
  const latestCostPrefilledRef = useRef(false)

  const { data, isLoading: isCompletedPondsLoading } = useGetCompletedPonds()
  // `/completed-ponds` only filters by cycle status, not by the caller's
  // pond-level permission scope. `/ponds/name` (the same source the sidebar
  // pond switcher uses, MenuPond.tsx) already reflects what this employee
  // can access, so intersect against it as a stand-in until completed-ponds
  // gets the same scope filtering server-side — see DOF ticket for the
  // real backend fix.
  const { data: pondNames, isLoading: isPondNamesLoading } = useGetPondNames()
  const isPondsLoading = isCompletedPondsLoading || isPondNamesLoading

  let ponds = data?.ponds ?? []
  if (pondNames) {
    const accessiblePondIds = new Set(pondNames.map((pond) => pond.id))
    ponds = ponds.filter((pond) => accessiblePondIds.has(pond.id))
  }
  if (record && record.destination_pond_id && mode === 'edit') {
    const exists = ponds.some((pond: any) => pond.id === record.destination_pond_id)
    if (!exists) {
      ponds = [
        ...ponds,
        {
          id: record.destination_pond_id,
          name: record.destination_pond_name,
        },
      ]
    }
  }

  const createShrimpTransferMutation = useMutateCreateShrimpTransfer()
  const updateShrimpTransferMutation = useMutateUpdateShrimpTransfer()

  const onSubmit = async (data: FormValues) => {
    const transferDateTime = getDateFromInputDateTime(data.transfer_date, data.transfer_time)

    const costData = {
      transfer_date: formatUTCDateTime(transferDateTime),
      amount: Number(data.amount),
      cost_per_kg: Number(data.cost_per_kg),
      destination_pond_id: data.destination_pond_id,
      amount_per_kg: Number(data.amount_per_kg),
    }

    try {
      if (record?.id && mode === 'edit') {
        await updateShrimpTransferMutation.mutateAsync({
          partialTransferId: record.id,
          body: costData,
        })
      } else {
        await createShrimpTransferMutation.mutateAsync({
          body: costData,
        })
      }
      invalidateCostQueries()
      onClose()
      if (onViewDialogClose) onViewDialogClose()
    } catch (error: any) {
      const rawMessage = error?.response?.data?.message || ''
      const code = rawMessage.split(':').pop()?.trim() // ได้ "NOT_ENOUGH_SHRIMP"
      if (code === 'NOT_ENOUGH_SHRIMP') {
        setError('amount', {
          type: 'server',
          message: t('transfer.not_enough_shrimp'),
        })
      }
    }
  }

  useEffect(() => {
    if (mode === 'edit' && record) {
      const recordDate = record.transfer_date ? new Date(record.transfer_date) : new Date()
      reset({
        transfer_date: recordDate,
        transfer_time: recordDate,
        destination_pond_id: record.destination_pond_id ?? '',
        amount: record.amount ?? '',
        cost_per_kg: record.cost_per_kg ?? '',
        amount_per_kg: record.amount_per_kg ?? '',
      })
    } else if (mode === 'create') {
      reset({
        transfer_date: new Date(),
        transfer_time: new Date(),
        destination_pond_id: '',
        amount: '',
        cost_per_kg: '',
        amount_per_kg: '',
      })
    }
  }, [mode, record, reset])

  useEffect(() => {
    if (mode !== 'create') return
    if (open) {
      refetchLatestCostPerKg()
    } else {
      latestCostPrefilledRef.current = false
    }
  }, [mode, open, refetchLatestCostPerKg])

  useEffect(() => {
    if (mode !== 'create' || !open || latestCostPrefilledRef.current || isDirty) return
    if (!latestCostPerKg?.cost_per_kg) return
    latestCostPrefilledRef.current = true
    const roundedCostPerKg = Math.round(latestCostPerKg.cost_per_kg * 100) / 100
    reset((values) => ({ ...values, cost_per_kg: roundedCostPerKg }))
  }, [mode, open, latestCostPerKg, reset, isDirty])

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="shrimp-transfer"
      title={record && mode === 'edit' ? t('transfer.edit') : t('transfer.records')}
      isSubmitting={
        createShrimpTransferMutation.isLoading || updateShrimpTransferMutation.isLoading
      }
    >
      <form id="shrimp-transfer" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography sx={{ minWidth: 100, mr: 2 }}>{t('transfer.date')}</Typography>
            <Box display="flex" gap={2} mt={2} mb={1}>
              {/* Date */}
              <Box flex={1}>
                <Typography fontSize={16}>{t('transfer.dateDay')}</Typography>
                <Controller
                  name="transfer_date"
                  control={control}
                  render={({ field }) => (
                    <ThaiDatePicker
                      format="dd/MM/yyyy"
                      value={field.value}
                      onChange={field.onChange}
                      maxDate={endOfToday()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.transfer_date,
                          helperText: errors.transfer_date?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Time */}
              <Box flex={1}>
                <Typography fontSize={16}>{t('transfer.timeDay')}</Typography>
                <Controller
                  name="transfer_time"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      ampm={false}
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.transfer_time,
                          helperText: errors.transfer_time?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" mt={2} mb={1}>
            <Typography sx={{ minWidth: 100, mr: 2 }}>{t('transfer.to', { species })}</Typography>
            <Controller
              name="destination_pond_id"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  {...field}
                  fullWidth
                  margin="none"
                  error={!!errors.destination_pond_id}
                  helperText={errors.destination_pond_id ? errors.destination_pond_id.message : ''}
                  id="shrimp-transfer-destination"
                  SelectProps={{ native: false }}
                  disabled={isPondsLoading}
                >
                  {ponds?.map((pond: any) => (
                    <MenuItem key={pond.id} value={pond.id}>
                      {pond.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mt={2} mb={1}>
            <Typography sx={{ minWidth: 100, mr: 2 }}>
              {t('transfer.shrimpSize', { species })}
            </Typography>
            <Controller
              name="amount_per_kg"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="none"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {t(species === SpeciesName.Fish ? 'transfer.gram' : 'transfer.amountPerKg')}
                      </InputAdornment>
                    ),
                    inputMode: 'numeric',
                  }}
                  type="number"
                  error={!!errors.amount_per_kg}
                  helperText={errors.amount_per_kg ? errors.amount_per_kg.message : ''}
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mt={2} mb={1}>
            <Typography sx={{ minWidth: 100, mr: 2 }}>{t('transfer.quantity')}</Typography>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="none"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{t('transfer.kg')}</InputAdornment>
                    ),
                    inputMode: 'numeric',
                  }}
                  type="number"
                  error={!!errors.amount}
                  helperText={errors.amount ? errors.amount.message : ''}
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mt={2} mb={1}>
            <Typography sx={{ minWidth: 100, mr: 2 }}>
              {isFish ? t('transfer.cost.per.one') : t('transfer.cost.per.kg')}
            </Typography>
            <Controller
              name="cost_per_kg"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="none"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{t('transfer.baht')}</InputAdornment>
                    ),
                    inputMode: 'numeric',
                  }}
                  type="number"
                  error={!!errors.cost_per_kg}
                  helperText={errors.cost_per_kg ? errors.cost_per_kg.message : ''}
                />
              )}
            />
          </Box>
        </DialogContent>
      </form>
    </BaseRecordDialog>
  )
}
export default ShrimpTransferRecordDialog
